/* tslint:disable:max-line-length */
import { Injectable } from '@angular/core';
import {
  DailyLogOperationInstance,
  StepInstance
} from '../../models/projects/operation.models';
import { TimeZoneService } from '../time-zone/time-zone.service';
import { PtLuxonDate } from '../../models/projects/date.models';
import { DateService } from '../date/date.service';
import { cloneDeep } from 'lodash';
import { DateTime } from 'luxon';

@Injectable()
export class MultipleDayService {

  constructor(
    public timeZoneService: TimeZoneService,
    public dateService: DateService
  ) {
  }

  async adjustDays(
    instance: DailyLogOperationInstance,
    position?: number,
    datePicker?: boolean,
    initialSetup?: boolean,
    currentDate?: DateTime // this is used to ensure that calculated dates don't get set on a previous day
  ) {
    if (position > 0) {
      position--;
    }

    const startTimeZone = await this.timeZoneService.getEditorLocally({
      dateTime: instance.startTime.dateTime,
      dateAmbiguous: false
    });

    let currentBase = new PtLuxonDate(null);

    if (!!instance.startTime.dateTime && !!startTimeZone) {
      currentBase = cloneDeep(new PtLuxonDate(cloneDeep(instance.startTime.getDate())));
    }

    let totalDayCount = 0;
    let dayCount = 0;

    if (!instance.startTimeToShow) {
      return dayCount;
    }

    const steps = instance.stepCollectionInstance != null
      ? instance.stepCollectionInstance.stepInstances : null;

    if (!steps || steps.length < 1) {
      if (initialSetup) {
        if (await this.dateService.isFirstDayLargerOffsetsPreApplied(instance.endTime.dateTime, instance.startTime.getDate())) {
          dayCount++;
        }
      } else {
        if (await this.dateService.isFirstDayLargerOffsetsPreApplied(instance.endTime.dateTime, instance.startTime.getDate()) ||
          await this.dateService.isFirstDateLargerOffsetsPreApplied(instance.startTime.dateTime, instance.endTime.getDate())) {
          dayCount++;
        }
      }

      await instance.endTime.setDay(dayCount, this.timeZoneService, currentBase, true, currentDate);
      return;
    }

    const firstStepPosition = this.findAdjacentStep(instance, -1);

    if (firstStepPosition != null) {
      if (await this.dateService.isFirstDayLargerOffsetsPreApplied(steps[firstStepPosition].startTime.dateTime, instance.startTime.getDate()) ||
        await this.dateService.isFirstDateLargerOffsetsPreApplied(instance.startTime.dateTime, steps[firstStepPosition].startTime.getDate())) {
        dayCount++;
      }
    }

    for (let i = 0; i < steps.length; i++) {
      if (steps[i].exclude) {
        continue;
      }

      if ((!steps[i].startConfirmed || position === i) && !datePicker) {
        await steps[i].startTime.setDay(dayCount, this.timeZoneService, currentBase, true, currentDate);
      } else {
        currentBase = cloneDeep(steps[i].startTime);
        totalDayCount += dayCount;
        dayCount = 0;
      }

      if (initialSetup) {
        if (await this.dateService.isFirstDayLargerOffsetsPreApplied(steps[i].endTime.dateTime, steps[i].startTime.getDate())) {
          dayCount++;
        }
      } else {
        if (await this.dateService.isFirstDayLargerOffsetsPreApplied(steps[i].endTime.dateTime, steps[i].startTime.getDate()) ||
          await this.dateService.isFirstDateLargerOffsetsPreApplied(steps[i].startTime.dateTime, steps[i].endTime.getDate())) {
          dayCount++;
        }
      }

      if ((!steps[i].endConfirmed || position === i) && !datePicker) {
        await steps[i].endTime.setDay(dayCount, this.timeZoneService, currentBase, true, currentDate);
      } else {
        currentBase = cloneDeep(steps[i].endTime);
        dayCount = 0;
        totalDayCount += dayCount;
      }

      const nextStepPosition = this.findAdjacentStep(instance, i);

      if (!nextStepPosition) {
        break;
      }

      if (initialSetup) {
        if (await this.dateService.isFirstDayLargerOffsetsPreApplied(steps[nextStepPosition].startTime.dateTime, steps[i].endTime.getDate())) {
          dayCount++;
        }
      } else {
        if (await this.dateService.isFirstDayLargerOffsetsPreApplied(steps[nextStepPosition].startTime.dateTime, steps[i].endTime.getDate()) ||
          await this.dateService.isFirstDateLargerOffsetsPreApplied(steps[i].endTime.dateTime, steps[nextStepPosition].startTime.getDate())) {
          dayCount++;
        }
      }
    }
  }

  async workOutEndDate(
    instance: DailyLogOperationInstance,
    bypassConfirm?: boolean,
    minimumDate?: DateTime,
    initialLoad?: boolean,
    startTimeCopy?: PtLuxonDate,
    endChanged?: boolean,
    editingOperationEnd?: boolean
  ) {
    if (instance.endConfirmed && !bypassConfirm) {
      return;
    }

    const steps = instance.stepCollectionInstance
      ? instance.stepCollectionInstance.stepInstances
      : null;

    if (!instance.startTimeToShow && !steps) {
      instance.endTime.setDate(null);
      instance.endTimeToShow = '';
      return;
    }

    const adjacentStepIndex = this.findAdjacentStep(instance, steps ? steps.length : 0, true);

    const dateBeforeEnd = steps && steps.length > 0 && adjacentStepIndex !== null && adjacentStepIndex !== -1
      ? steps[adjacentStepIndex].endTime
      : instance.startTime;

    if (initialLoad && dateBeforeEnd && !instance.endTime.dateTime && (!steps || steps.length < 1)) {
      if (instance.projectSpecificOperation.operationSummary.estimatedDurationSeconds != null) {
        instance.endTime = new PtLuxonDate(await this.dateService.addDuration(dateBeforeEnd.dateTime, {second: instance.projectSpecificOperation.operationSummary.estimatedDurationSeconds}));
      } else {
        instance.endTime = cloneDeep(dateBeforeEnd);
      }
    } else if (!endChanged && await this.dateService.isFirstDateLargerOffsetsPreApplied(dateBeforeEnd.dateTime, instance.endTime.dateTime)) {
      if (instance.endTime.dateTime && startTimeCopy.dateTime) {
        // step > end
        const difference = (instance.endTime.dateTime.toMillis() - startTimeCopy.dateTime.toMillis()) % 86400000;
        instance.endTime = new PtLuxonDate(await this.dateService.addDuration(dateBeforeEnd.dateTime, {millisecond: difference}));
      }
    } else if (!endChanged && dateBeforeEnd && await this.dateService.isFirstDateLargerOffsetsPreApplied(instance.endTime.dateTime, dateBeforeEnd.dateTime) && (!steps || steps.length === 0)) {
      // end > step
      // modulo gets difference between hours, we don't care about any day changes
      const difference = (instance.endTime.dateTime.toMillis() - (startTimeCopy && startTimeCopy.dateTime
        ? startTimeCopy.dateTime.toMillis()
        : dateBeforeEnd.dateTime.toMillis())) % 86400000;

      instance.endTime = new PtLuxonDate(await this.dateService.addDuration(dateBeforeEnd.dateTime, {millisecond: difference}));
    } else if (!endChanged && !instance.endTime.dateTime && dateBeforeEnd && dateBeforeEnd.dateTime) {
      const difference = instance.projectSpecificOperation && instance.projectSpecificOperation.operationSummary
        ? instance.projectSpecificOperation.operationSummary.estimatedDurationSeconds * 1000
        : 0;

      instance.endTime = new PtLuxonDate(await this.dateService.addDuration(dateBeforeEnd.dateTime, {millisecond: difference}));
    }

    // if end is before the datebefore after all the above, then it needs to be moved onto the next day
    if (instance.endTime && await this.dateService.isFirstDateLargerOffsetsPreApplied(dateBeforeEnd.dateTime, instance.endTime.dateTime)) {
      // step > end
      instance.endTime = await this.dateService.incrementDay(instance.endTime);
    }

    await instance.endTime.checkForMultipleOffsets(this.timeZoneService, instance.endTimeOffset);

    // if multiple offsets then just set the offset of the step before end, user can change it manually if they want
    if (instance.endTime.multipleOffsets && instance.endTime.multipleOffsets.result && instance.endTime.offsetNull() && instance.endTimeOffset != null) {
      instance.endTime.setOffset(dateBeforeEnd && dateBeforeEnd.dateTime ? dateBeforeEnd.dateTime.offset * 60 : null);
    }

    if (minimumDate && instance.endTime && instance.endTime.dateTime && instance.endTime.dateTime.toMillis() < minimumDate.toMillis()) {
      instance.endTime.dateTime = cloneDeep(minimumDate);
    }

    // add operation end delayed entry if it has been added for this step collection
    // if editing operation end directly, don't add the delayedEntry
    if (!editingOperationEnd
      && instance.projectSpecificOperation.operationSummary.stepCollection
      && instance.projectSpecificOperation.operationSummary.stepCollection.operationEndDelayedEntrySeconds) {
      instance.endTime.dateTime = await this.dateService.addDuration(instance.endTime.dateTime,
        {seconds: instance.projectSpecificOperation.operationSummary.stepCollection.operationEndDelayedEntrySeconds});
    }

    instance.endTimeToShow = this.dateService.setTimeStringFromPtLuxonDate(instance.endTime);

    // if end time is invalid, just null it all out and let the user sort it manually, i think this happens when this
    // clashes with a tz change. Before it was throwing a console error, so it's better this way even if its not perfect
    if (instance.endTimeToShow == "Invalid DateTime") {
      instance.endTime = new PtLuxonDate(null, true);
      instance.endTimeOffset = null;
      instance.endTimeToShow = null;
    }
  }

  isFirstTimeToShowBigger(firstTimeToShow: string, secondTimeToShow: string): boolean {
    if (!firstTimeToShow || !secondTimeToShow || firstTimeToShow.length !== 5 || secondTimeToShow.length !== 5) {
      return false;
    }

    const firstIndex = firstTimeToShow.indexOf(':');
    const firstHours = firstTimeToShow.substring(firstIndex - 2, firstIndex);
    const firstMinutes = firstTimeToShow.substring(firstIndex + 1, firstIndex + 3);
    const firstTotalMinutes = Number.parseInt(firstHours, 10) * 60 + Number.parseInt(firstMinutes, 10);

    const secondIndex = secondTimeToShow.indexOf(':');
    const secondHours = secondTimeToShow.substring(secondIndex - 2, secondIndex);
    const secondMinutes = secondTimeToShow.substring(secondIndex + 1, secondIndex + 3);
    const secondTotalMinutes = Number.parseInt(secondHours, 10) * 60 + Number.parseInt(secondMinutes, 10);

    return firstTotalMinutes > secondTotalMinutes;
  }

  findAdjacentStep(instance: DailyLogOperationInstance, position: number, backwards?: boolean): number {
    if (!instance) {
      return null;
    }

    if (!instance.stepCollectionInstance) {
      return null;
    }

    const stepInstances = instance.stepCollectionInstance.stepInstances;

    if (!stepInstances) {
      return null;
    }

    if (backwards) {
      return this.workBackwardsToFindStepPosition(instance, position, stepInstances);
    } else {
      return this.workForwardsToFindStepPosition(instance, position, stepInstances);
    }
  }

  workBackwardsToFindStepPosition(instance: DailyLogOperationInstance, position: number, stepInstances: StepInstance[]): number {
    if (position === 0) {
      return null;
    }

    for (let i = (position - 1); i >= 0; i--) {
      if (!stepInstances[i].exclude) {
        return i;
      }
    }

    return null;
  }

  workForwardsToFindStepPosition(instance: DailyLogOperationInstance, position: number, stepInstances: StepInstance[]): number {
    if (position >= stepInstances.length - 1) {
      return null;
    }

    for (let i = (position + 1); i < stepInstances.length; i++) {
      if (!stepInstances[i].exclude) {
        return i;
      }
    }

    return null;
  }

}



// WEBPACK FOOTER //
// ./src/services/mutiple-day/multiple-day.service.ts